// SimAdminDashboard.js — Admin page for simulation ingestion monitoring,
// alerts, manual overrides, and fee configuration.

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { AlertTriangle, CheckCircle, Database, Upload, RefreshCw, Clock, Zap } from 'lucide-react';
import LiveArbitrageExperiment from './live-experiment/LiveArbitrageExperiment';

function AlertRow({ alert, onAcknowledge }) {
  const severityColors = {
    critical: 'bg-red-100 text-red-800',
    warning: 'bg-amber-100 text-amber-800',
    info: 'bg-blue-100 text-blue-800',
  };

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${alert.acknowledged ? 'bg-gray-50' : 'bg-white border border-gray-200'}`}>
      <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
        alert.severity === 'critical' ? 'text-red-500' :
        alert.severity === 'warning' ? 'text-amber-500' : 'text-blue-500'
      }`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${severityColors[alert.severity] || 'bg-gray-100'}`}>
            {alert.severity}
          </span>
          <span className="text-xs text-gray-400">{alert.series_key}</span>
          <span className="text-xs text-gray-400 ml-auto">{alert.created_at?.split('T')[0]}</span>
        </div>
        <p className="text-sm text-gray-700 mt-1">{alert.message}</p>
      </div>
      {!alert.acknowledged && (
        <button
          onClick={() => onAcknowledge(alert.id)}
          className="text-xs text-gray-500 hover:text-gray-700 flex-shrink-0"
        >
          <CheckCircle className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function IngestionRow({ item }) {
  const stale = item.staleness_days > 3;

  return (
    <tr className="border-b border-gray-50">
      <td className="py-2 px-3 text-xs font-mono">{item.series_key}</td>
      <td className="py-2 px-3 text-xs text-gray-500">{item.source}</td>
      <td className="py-2 px-3 text-xs font-mono">{item.latest_date || 'None'}</td>
      <td className="py-2 px-3 text-xs text-right">{item.total_observations || 0}</td>
      <td className={`py-2 px-3 text-xs text-right font-mono ${stale ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
        {item.staleness_days != null ? `${item.staleness_days}d` : '-'}
      </td>
      <td className="py-2 px-3">
        <span className={`w-2 h-2 rounded-full inline-block ${item.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
      </td>
    </tr>
  );
}

export default function SimAdminDashboard() {
  const [activeTab, setActiveTab] = useState('ingestion');
  const [alerts, setAlerts] = useState([]);
  const [ingestion, setIngestion] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Rate override form
  const [overrideForm, setOverrideForm] = useState({
    seriesKey: '', observationDate: '', value: '', valueType: 'close',
  });

  // CSV upload
  const [csvForm, setCsvForm] = useState({ csvData: '', targetTable: 'rate_values', seriesKey: '' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [alertsRes, ingestionRes, auditRes] = await Promise.all([
        api.get('/sim-admin/alerts').catch(() => ({ success: false })),
        api.get('/sim-admin/ingestion-status').catch(() => ({ success: false })),
        api.get('/sim-admin/audit-log?limit=50').catch(() => ({ success: false })),
      ]);

      if (alertsRes.success) setAlerts(alertsRes.data);
      if (ingestionRes.success) setIngestion(ingestionRes.data);
      if (auditRes.success) setAuditLog(auditRes.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAcknowledge = async (alertId) => {
    try {
      await api.patch(`/sim-admin/alerts/${alertId}/acknowledge`);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, acknowledged: true } : a));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOverride = async () => {
    if (!overrideForm.seriesKey || !overrideForm.observationDate || !overrideForm.value) return;
    try {
      const res = await api.post('/sim-admin/rate-override', overrideForm);
      if (res.success) {
        setOverrideForm({ seriesKey: '', observationDate: '', value: '', valueType: 'close' });
        loadData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvForm.csvData || !csvForm.targetTable) return;
    try {
      const res = await api.post('/sim-admin/csv-upload', csvForm);
      if (res.success) {
        setCsvForm({ csvData: '', targetTable: 'rate_values', seriesKey: '' });
        loadData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const tabs = [
    { key: 'ingestion', label: 'Ingestion Status', icon: Database },
    { key: 'alerts', label: `Alerts (${alerts.filter(a => !a.acknowledged).length})`, icon: AlertTriangle },
    { key: 'overrides', label: 'Manual Override', icon: Upload },
    { key: 'audit', label: 'Audit Log', icon: Clock },
    { key: 'live-experiment', label: 'Live Experiment', icon: Zap },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Simulator Admin</h1>
          <p className="text-sm text-gray-500 mt-1">Data ingestion, alerts, and manual overrides</p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-4">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Ingestion Status */}
      {activeTab === 'ingestion' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">Series</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">Source</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">Latest Date</th>
                <th className="text-right py-2.5 px-3 text-gray-500 font-medium">Count</th>
                <th className="text-right py-2.5 px-3 text-gray-500 font-medium">Staleness</th>
                <th className="py-2.5 px-3 text-gray-500 font-medium">Active</th>
              </tr>
            </thead>
            <tbody>
              {ingestion.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400">No series data</td></tr>
              ) : (
                ingestion.map((item, idx) => <IngestionRow key={idx} item={item} />)
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Alerts */}
      {activeTab === 'alerts' && (
        <div className="space-y-2">
          {alerts.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No alerts</div>
          ) : (
            alerts.map(alert => (
              <AlertRow key={alert.id} alert={alert} onAcknowledge={handleAcknowledge} />
            ))
          )}
        </div>
      )}

      {/* Manual Override */}
      {activeTab === 'overrides' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Rate override */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Insert Rate Value</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Series Key</label>
                <input
                  type="text" value={overrideForm.seriesKey}
                  onChange={e => setOverrideForm(p => ({ ...p, seriesKey: e.target.value }))}
                  placeholder="e.g. KE.FX.USD.SPOT"
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date</label>
                <input
                  type="date" value={overrideForm.observationDate}
                  onChange={e => setOverrideForm(p => ({ ...p, observationDate: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Value</label>
                <input
                  type="number" step="0.00000001" value={overrideForm.value}
                  onChange={e => setOverrideForm(p => ({ ...p, value: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                />
              </div>
              <button onClick={handleOverride}
                className="w-full py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800">
                Insert Override
              </button>
            </div>
          </div>

          {/* CSV upload */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">CSV Upload</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Target Table</label>
                <select
                  value={csvForm.targetTable}
                  onChange={e => setCsvForm(p => ({ ...p, targetTable: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="rate_values">Rate Values</option>
                  <option value="auctions">Auctions</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Series Key (for rate values)</label>
                <input
                  type="text" value={csvForm.seriesKey}
                  onChange={e => setCsvForm(p => ({ ...p, seriesKey: e.target.value }))}
                  placeholder="e.g. KE.TBILL.91"
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">CSV Data</label>
                <textarea
                  value={csvForm.csvData}
                  onChange={e => setCsvForm(p => ({ ...p, csvData: e.target.value }))}
                  placeholder="date,value&#10;2024-01-01,16.245&#10;2024-01-08,16.100"
                  rows={6}
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                />
              </div>
              <button onClick={handleCsvUpload}
                className="w-full py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800">
                Upload CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live Experiment */}
      {activeTab === 'live-experiment' && (
        <LiveArbitrageExperiment />
      )}

      {/* Audit Log */}
      {activeTab === 'audit' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">Time</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">Action</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">Entity</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLog.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 text-gray-400">No audit entries</td></tr>
              ) : (
                auditLog.map((entry, idx) => (
                  <tr key={idx} className="border-b border-gray-50">
                    <td className="py-2 px-3 font-mono text-gray-500">{entry.created_at?.replace('T', ' ').slice(0, 19)}</td>
                    <td className="py-2 px-3 font-medium">{entry.action}</td>
                    <td className="py-2 px-3 text-gray-600">{entry.entity_type}:{entry.entity_id}</td>
                    <td className="py-2 px-3 text-gray-500 font-mono truncate max-w-xs">
                      {typeof entry.details === 'string' ? entry.details : JSON.stringify(entry.details)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
